/**
 * DemoLivePulse - Atmospheric "system heartbeat" strip
 * 
 * Slimmer header with clear visual separation from content below.
 * Shows 2 events only for compact display.
 * 
 * GUARDRAIL: This component uses only static demo data.
 * No imports from hooks/, features/, or services/.
 */

import { getDemoPulseEvents } from "@/lib/demoData";
import { cn } from "@/lib/utils";
import { RoleIcon, type LogRole } from "@/components/ui/RoleIcon";
import { useLanguage } from "@/contexts/LanguageContext";
import { useMemo } from "react";

interface DemoLivePulseProps {
  embedded?: boolean;
}

export function DemoLivePulse({ embedded = false }: DemoLivePulseProps) {
  const { t } = useLanguage();
  const pulseEvents = useMemo(() => getDemoPulseEvents(t), [t]);
  
  // Show first 3 events - payment, delivery, status change
  const eventsToShow = pulseEvents.slice(0, 3);
  
  return (
    <div className={cn(
      "relative h-[64px] overflow-hidden",
      embedded 
        ? "bg-muted/20 border-t border-border/30" 
        : "bg-muted/40 border border-border/40 rounded-xl shadow-sm"
    )}>
      {/* Heartbeat indicator */}
      <div className="absolute top-2 right-3 flex items-center gap-1.5">
        <span className="w-2 h-2 rounded-full bg-primary pulse-heartbeat" />
      </div>
      
      {/* Event rows with fading opacity */}
      <div className="px-3 py-1.5 space-y-0.5">
        {eventsToShow.map((event, index) => {
          const opacity = index === 0 ? 1 : index === 1 ? 0.6 : 0.35;
          
          // Determine text color based on event type
          const getMessageClass = () => {
            if (event.isPaid) return "text-emerald-600 dark:text-emerald-400 text-[11px] font-medium";
            if (event.isDelivered) return "text-emerald-600 dark:text-emerald-400 text-[11px]";
            return "text-foreground text-[11px]";
          };
          
          return (
            <div
              key={`${event.orderNumber}-${index}`}
              className="flex items-center gap-2 text-xs"
              style={{ opacity }}
            >
              <span className="font-mono text-muted-foreground w-9 text-[11px]">
                {event.timestamp}
              </span>
              <RoleIcon role={event.role as LogRole} size="sm" className="flex-shrink-0" />
              <span className="text-muted-foreground font-mono text-[11px]">
                {event.orderNumber}
              </span>
              <span className={getMessageClass()}>
                {event.message}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}